import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, ArrowDown } from 'lucide-react';
interface MethodSectionProps {
  onOpenForm: () => void;
}
export function MethodSection({
  onOpenForm
}: MethodSectionProps) {
  const scrollToComparison = () => {
    document.getElementById('comparison')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section id="method" className="py-24 lg:py-36 bg-white" data-id="element-395">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-396">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-397">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center px-3 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6" data-id="element-398">
            <span className="text-navy text-xs font-bold tracking-widest uppercase" data-id="element-399">
              The Reporting Framework
            </span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-navy mb-6 tracking-tight leading-tight" data-id="element-400">
            Credit reporting issues are solved through visibility,
            documentation, and timing
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16" data-id="element-401">
          {/* Card 1: ADD */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-offwhite rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-sm relative overflow-hidden flex flex-col h-full" data-id="element-402">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-emerald/5 rounded-full blur-[60px] pointer-events-none" data-id="element-403"></div>

            <div className="text-sm font-bold text-emerald uppercase tracking-widest mb-6" data-id="element-404">
              01 / Positive Reporting Infrastructure
            </div>

            <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-405">
              Add verified reporting activity first
            </h3>
            <p className="text-slate leading-[1.6] mb-8 flex-grow" data-id="element-406">
              Negative reporting receives most attention, but unstable files
              often fail long before disputes begin. Positive reporting
              consistency creates context for lenders, reduces volatility, and
              improves visibility into how bureau changes affect the file over
              time. This phase focuses on measurable reporting behavior, not
              quick-score tactics.
            </p>

            <ul className="space-y-4 mb-10" data-id="element-407">
              <li className="flex items-start text-navy font-medium" data-id="element-408">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-409">
                  <Check className="w-4 h-4 text-emerald" data-id="element-410" />
                </div>
                <span className="leading-snug" data-id="element-411">
                  Establish consistent on-time reporting activity
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-412">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-413">
                  <Check className="w-4 h-4 text-emerald" data-id="element-414" />
                </div>
                <span className="leading-snug" data-id="element-415">
                  Monitor utilization across revolving accounts
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-416">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-417">
                  <Check className="w-4 h-4 text-emerald" data-id="element-418" />
                </div>
                <span className="leading-snug" data-id="element-419">
                  Compare bureau reporting discrepancies weekly
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-420">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-421">
                  <Check className="w-4 h-4 text-emerald" data-id="element-422" />
                </div>
                <span className="leading-snug" data-id="element-423">
                  Track FICO 8 movement across all three bureaus
                </span>
              </li>
            </ul>

            <button onClick={scrollToComparison} className="inline-flex items-center text-emerald font-bold hover:text-emerald-600 transition-colors group mt-auto" data-id="element-424">
              See top builders
              <ArrowDown className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform" data-id="element-425" />
            </button>
          </motion.div>

          {/* Card 2: REMOVE */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-offwhite rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-sm relative overflow-hidden flex flex-col h-full" data-id="element-426">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-red-500/5 rounded-full blur-[60px] pointer-events-none" data-id="element-427"></div>

            <div className="text-sm font-bold text-red-600 uppercase tracking-widest mb-6" data-id="element-428">
              02 / Consumer-Directed Investigation
            </div>

            <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-429">
              Challenge reporting inconsistencies with evidence
            </h3>
            <p className="text-slate leading-[1.6] mb-8 flex-grow" data-id="element-430">
              Consumers have the right to dispute inaccurate or incomplete
              reporting under FCRA §1681i. Effective disputes rely on
              documentation, timelines, and bureau-specific inconsistencies
              rather than emotional language or mass-produced templates. The
              consumer reviews, approves, and submits every dispute directly.
            </p>

            <ul className="space-y-4 mb-10" data-id="element-431">
              <li className="flex items-start text-navy font-medium" data-id="element-432">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-433">
                  <Check className="w-4 h-4 text-red-600" data-id="element-434" />
                </div>
                <span className="leading-snug" data-id="element-435">
                  Identify mismatched balances and reporting dates
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-436">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-437">
                  <Check className="w-4 h-4 text-red-600" data-id="element-438" />
                </div>
                <span className="leading-snug" data-id="element-439">
                  Archive bureau responses and correspondence
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-440">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-441">
                  <Check className="w-4 h-4 text-red-600" data-id="element-442" />
                </div>
                <span className="leading-snug" data-id="element-443">
                  Compare furnisher reporting across all bureaus
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-444">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-445">
                  <Check className="w-4 h-4 text-red-600" data-id="element-446" />
                </div>
                <span className="leading-snug" data-id="element-447">
                  Draft disputes using documented evidence only
                </span>
              </li>
            </ul>

            <button onClick={onOpenForm} className="inline-flex items-center text-red-600 font-bold hover:text-red-700 transition-colors group mt-auto" data-id="element-448">
              Get the free dispute guide
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-449" />
            </button>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-center max-w-3xl mx-auto" data-id="element-450">
          <p className="text-slate font-medium italic leading-[1.6]" data-id="element-451">
            Public complaint records reveal the same reporting patterns
            appearing across thousands of consumer files — patterns most people
            only notice after they've already been denied.
          </p>
        </motion.div>
      </div>
    </section>;
}